import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, Alert, useTheme, useMediaQuery
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useAuth } from '../../contexts/AuthContext';
import { usersAPI } from '../../services/api';
import SlideTransition from './SlideTransition';
import DevBadge from './DevBadge';

export default function ProfileCompletionModal() {
  const { user, fetchCurrentUser } = useAuth();
  const needsCompletion = user && (!user.name || !user.phone);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [form, setForm] = useState({ name: user?.name || '', lastName: user?.lastName || '', phone: user?.phone || '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  if (!needsCompletion) return null;

  const validatePhone = (val) => {
    if (!val) return '';
    if (val.length < 7) return 'Mínimo 7 caracteres.';
    if (val.length > 15) return 'Máximo 15 caracteres.';
    return '';
  };

  const handlePhoneChange = (val) => {
    setForm(f => ({ ...f, phone: val }));
    setPhoneError(validatePhone(val));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      setError('El nombre y el teléfono son obligatorios.');
      return;
    }
    const pe = validatePhone(form.phone);
    if (pe) { setPhoneError(pe); return; }
    setError('');
    setSaving(true);
    try {
      await usersAPI.update(user._id, { name: form.name.trim(), lastName: form.lastName.trim(), phone: form.phone.trim() });
      await fetchCurrentUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar. Intenta nuevamente.');
      setSaving(false);
    }
  };

  return (
    <Dialog open disableEscapeKeyDown maxWidth="xs" fullWidth fullScreen={isMobile} TransitionComponent={SlideTransition}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1.5}>
          <AccountCircleIcon />
          <Typography variant="h6" fontWeight={600}>Completa tu perfil</Typography>
          <DevBadge id="modal:completar-perfil" inline />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Para continuar, por favor ingresa tu nombre y número de contacto.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" gap={2}>
            <TextField
              label="Nombre *"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              fullWidth
              autoFocus
            />
            <TextField
              label="Apellido"
              value={form.lastName}
              onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
              fullWidth
            />
          </Box>
          <TextField
            label="Teléfono *"
            value={form.phone}
            onChange={e => handlePhoneChange(e.target.value)}
            fullWidth
            placeholder="+58 412 000 0000"
            error={!!phoneError}
            helperText={phoneError}
            inputProps={{ maxLength: 15 }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving} fullWidth>
          {saving ? 'Guardando...' : 'Guardar y continuar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
